import { useQuery } from '@tanstack/react-query';
import { useCurrentLocale } from '@/hooks/useCurrentLocale';
import { useLocalizations } from '@/hooks/useLocalizations';
import { logger } from '@/utils/logger';
import { getPracticeAreaLocalizationKey } from '@/lib/practiceAreaUtils';
import { resolveToPrimarySync } from '@/hooks/usePrimaryLocale';

export interface LocalizedPracticeArea {
  id: string;
  english: string;
  displayName: string;
  search: string;
}

interface BundledPracticeArea {
  id: string;
  english: string;
  names: Record<string, string>;
}

// Cache the bundled data to avoid refetching
let cachedPracticeAreas: BundledPracticeArea[] | null = null;
let fetchAttempted = false;

/**
 * Fetches bundled practice area data from static JSON and maps to current locale.
 */
async function fetchLocalizedPracticeAreas(locale: string): Promise<LocalizedPracticeArea[]> {
  try {
    // Fetch bundled data if not cached and not already attempted
    if (!cachedPracticeAreas && !fetchAttempted) {
      fetchAttempted = true;
      const response = await fetch('/data/practice-areas.json');
      if (!response.ok) {
        logger.warn('practice-areas.json not found, will use i18next fallback');
        return [];
      }
      cachedPracticeAreas = await response.json();
    }

    if (!cachedPracticeAreas || cachedPracticeAreas.length === 0) {
      return [];
    }

    // Resolve to primary locale (e.g., 'pt-BR' → 'pt', 'zh-HK' → 'zh-TW')
    // Note: JSON uses 36 primary language codes, resolveToPrimarySync handles special cases
    const primaryLocale = resolveToPrimarySync([], locale).toLowerCase();

    // Map to LocalizedPracticeArea with current locale's name
    const mapped = cachedPracticeAreas.map((pa): LocalizedPracticeArea => {
      // Use primary locale key directly (JSON uses 36 primary language codes)
      const translatedName = pa.names[primaryLocale] || pa.names['en'] || pa.english;

      // Include all translations in search terms for better matching
      const searchTerms = [pa.english, ...Object.values(pa.names)].filter(Boolean).join(' ');

      return {
        id: pa.id,
        english: pa.english,
        displayName: translatedName,
        search: searchTerms,
      };
    });

    // Sort alphabetically by translated name using locale-aware comparison
    return mapped.sort((a, b) => a.displayName.localeCompare(b.displayName, locale));
  } catch (error) {
    logger.error('Error loading practice areas', { error });
    return [];
  }
}

/**
 * Hook to fetch practice area data with user-friendly display names.
 * 
 * @description
 * This is the PRIMARY hook for USER-FACING UI that displays practice area names.
 * Uses React Query for caching and loads from static JSON for performance.
 * Falls back to i18next keys if the JSON file is not available yet.
 * 
 * **Data Sources:**
 * - Primary: Static JSON file at `/data/practice-areas.json` (bundled with all translations)
 * - Fallback: i18next keys from `components:practice_areas.*`
 * 
 * **When to use:**
 * - Practice area dropdowns and selectors in user-facing UI
 * - Any component showing practice area names to end users
 * - Practice area badges and labels
 * 
 * @returns {{ practiceAreas: LocalizedPracticeArea[], isLoading: boolean, error: Error | null, getDisplayName: Function }}
 * 
 * @example
 * const { practiceAreas, getDisplayName } = useLocalizedPracticeAreas();
 * // To get display name by English name:
 * getDisplayName("Accounting") // → "Comptabilité" (for French locale)
 */
interface UseLocalizedPracticeAreasOptions {
  /** Sort by English name instead of translated name (for admin use) */
  sortByEnglish?: boolean;
}

export function useLocalizedPracticeAreas(options: UseLocalizedPracticeAreasOptions = {}) {
  const { sortByEnglish = false } = options;
  const { lComponents: t } = useLocalizations();
  const locale = useCurrentLocale();

  const { data: practiceAreas = [], isLoading, error } = useQuery({
    queryKey: ['localized-practice-areas', locale],
    queryFn: () => fetchLocalizedPracticeAreas(locale),
    staleTime: Infinity,  // Static file - never refetch during session
    gcTime: 60 * 60 * 1000,  // Keep in memory for 1 hour
    select: (data) => {
      if (sortByEnglish) {
        // Re-sort by English name for admin
        return [...data].sort((a, b) => a.english.localeCompare(b.english, 'en'));
      }
      return data;
    },
  });

  /**
   * Get the localized display name for a practice area given its English name.
   * Falls back to i18next if JSON data is not available, then to the English name.
   */
  const getDisplayName = (englishName: string | null | undefined): string => {
    if (!englishName) return '';
    
    // Try JSON data first
    const found = practiceAreas.find(pa => pa.english === englishName);
    if (found) {
      return found.displayName ?? englishName;
    }
    
    // Fallback to i18next keys (legacy support)
    const key = getPracticeAreaLocalizationKey(englishName);
    const translated = t(`practice_areas.${key}`, { defaultValue: englishName });
    
    // Defensive: ensure we always return a string
    return typeof translated === 'string' ? translated : englishName;
  };

  /**
   * Get the localized display name for a practice area given its ID.
   * Falls back to empty string if not found.
   */
  const getDisplayNameById = (id: string | null | undefined): string => {
    if (!id) return '';
    const found = practiceAreas.find(pa => pa.id === id);
    return found?.displayName || '';
  };

  return { 
    practiceAreas, 
    isLoading, 
    error,
    getDisplayName,
    getDisplayNameById,
  };
}
